import { buildNarrativeSuggestions, narrativeTranslationRules } from './narrativeTranslation';

// A draft is a set of choices, not an attestation. Keep every suggestion beside
// the exact self-reported excerpt that supports it; specialty ideas never become
// asserted experience. This adapter deliberately narrows the older rule fan-out.
const rulesById = Object.fromEntries(narrativeTranslationRules.map(rule => [rule.id, rule]));
const NEGATED = /\b(?:no|not|never|without|cannot|can't|didn't|did not|haven't|have not|wasn't|weren't|don't|doesn't|hasn't|hadn't)\b/i;
const ASPIRATIONAL = /\b(?:want to|hope to|plan to|would like to|interested in|aspir(?:e|ing) to|learning about|studying|seeking experience)\b/i;
const ACTION = /\b(?:support(?:ed|ing)?|work(?:ed|ing)?|help(?:ed|ing)?|manag(?:e|ed|ing)|maintain(?:ed|ing)?|perform(?:ed|ing)?|conduct(?:ed|ing)?|handl(?:e|ed|ing)|process(?:ed|ing)?|track(?:ed|ing)?|research(?:ed|ing)?|look(?:ed|ing)?|identif(?:y|ied|ying)|verif(?:y|ied|ying)|coordinat(?:e|ed|ing)|provid(?:e|ed|ing)|order(?:ed|ing)?|backorder(?:ed|ing)?|reconcil(?:e|ed|ing)|resolv(?:e|ed|ing)|initiat(?:e|ed|ing)|prioriti[sz](?:e|ed|ing)|inventoried|responsible for)\b/i;

const has = (source, pattern) => pattern.test(source);
const inventory = /\binventor(?:y|ies|ied)\b|accountab|verified? (?:stock )?balances?|asset locations?/i;
const inventoryWork = /\b(?:maintain(?:ed|ing)?|manag(?:e|ed|ing)|updat(?:e|ed|ing)|perform(?:ed|ing)?|conduct(?:ed|ing)?|support(?:ed|ing)?|handl(?:e|ed|ing)|track(?:ed|ing)?|verif(?:y|ied|ying)|reconcil(?:e|ed|ing)|responsible for)\b[^.!?]{0,60}(?:inventor|accountab|balances?|asset locations?)|\binventoried\b/i;
const material = /\b(?:parts?|materials?|suppl(?:y|ies)|stock|requisitions?|equipment|bags?)\b/i;
const parts = /aircraft[- ]parts?|aviation[- ]parts?|(?:\bc[- ]?17\b|aircraft|aviation)[\s\S]{0,60}(?:parts? store|parts? suppl|parts? support)|(?:parts? store|parts? suppl|parts? support)[\s\S]{0,60}(?:\bc[- ]?17\b|aircraft|aviation)/i;
const stockNumbers = /\b(?:national )?stock numbers?\b|\bnsns?\b/i;
const partNumbers = /\bpart numbers?\b/i;
const research = /research|look\w* (?:up|for)|search|identif|locat|cross[- ]referenc/i;
const ordering = /\border(?:s|ed|ing)?\b(?!\s+(?:status|numbers?|history|records?))|requisitions?/i;
const backordering = /\bback[- ]?order(?:s|ed|ing)?\b/i;
const deployment = /deployment (?:bags?|equipment)|mobility bags?|bags? for deployments?|\b[a-c](?:\s*[a-c]){2} bags?\b/i;
const protective = /jslist|chemical[- ]protective|protective equipment|cbrn equipment/i;

function sourceUnits(bio) {
  // Split at clear sentence/contrast boundaries. Each returned source remains a
  // literal substring of the saved bio, including its original capitalization.
  return (bio.match(/[^.!?;\n]+[.!?;]?/g) || [])
    .flatMap(sentence => sentence.split(/\b(?:but|however|although)\b/i))
    .flatMap(sentence => sentence.length > 420 ? sentence.split(/,/) : [sentence])
    .map(source => source.trim())
    .filter(source => source && !NEGATED.test(source) && !ASPIRATIONAL.test(source));
}

function stableId(type, theme, text) {
  // Content identity survives input ordering changes and equivalent rerenders.
  let hash = 2166136261;
  const value = text.toLocaleLowerCase('en-US');
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return `${type}-${theme}-${(hash >>> 0).toString(36)}`;
}

const themeAdapters = [
  {
    id: 'inventory-accountability', heading: 'Inventory and accountability',
    matches: source => has(source, inventory) && has(source, inventoryWork)
      && !(has(source, /inventory (?:system|software|database)/i) && !has(source, material)),
    bullet: source => has(source, /\binventor(?:y|ies|ied)\b/i)
      ? (has(source, /(?:maintain|manag|updat)\w*[^.!?]{0,35}inventory records?/i) ? 'Maintained inventory records.' : 'Supported inventory control.')
      : has(source, /verified? (?:stock )?balances?/i)
        ? 'Verified stock balances to support material accountability.'
        : has(source, /asset locations?/i)
          ? 'Tracked material locations.' : 'Supported material accountability.',
    skills: source => [
      has(source, /inventory|accountab/i) && 'Inventory accountability',
      has(source, /inventory/i) && 'Inventory control',
      has(source, /asset locations?|asset track/i) && 'Asset tracking',
    ],
    keywords: source => [
      has(source, /inventory|accountab/i) && 'inventory accountability',
      has(source, /asset locations?/i) && 'asset location',
      has(source, /balances?/i) && 'stock balance',
      has(source, /storage/i) && 'storage',
      has(source, /inspection/i) && 'inspection',
      has(source, /\bissue[ds]?\b/i) && 'material issue',
    ],
  },
  {
    id: 'aircraft-parts', heading: 'Aircraft parts support',
    matches: source => has(source, parts) && has(source, ACTION),
    bullet: source => has(source, /\bc[- ]?17\b/i)
      ? 'Supported aircraft-parts operations for C-17 maintenance requirements.'
      : 'Supported aircraft-parts operations for maintenance requirements.',
    skills: () => ['Aircraft parts support', 'Aviation logistics support'],
    keywords: source => [
      'aircraft parts', has(source, /aviation logistics/i) && 'aviation logistics',
      has(source, /maintain|maintenance/i) && 'maintenance support',
      has(source, /parts store/i) && 'parts store', has(source, /\bc[- ]?17\b/i) && 'C-17 aircraft parts',
    ],
  },
  {
    id: 'part-research', heading: 'Parts identification',
    matches: source => has(source, research) && (has(source, stockNumbers) || has(source, partNumbers) || has(source, /catalog research/i)),
    bullet: source => {
      const identifiers = [has(source, stockNumbers) && (has(source, /national stock|\bnsns?\b/i) ? 'national stock numbers' : 'stock numbers'), has(source, partNumbers) && 'part numbers'].filter(Boolean);
      return identifiers.length ? `Researched ${identifiers.join(' and ')} to identify requested material.` : 'Researched material catalogs.';
    },
    skills: source => [
      has(source, stockNumbers) && has(source, partNumbers) ? 'Stock-number and part-number research'
        : has(source, stockNumbers) ? (has(source, /national stock|\bnsns?\b/i) ? 'National stock number research' : 'Stock-number research') : has(source, partNumbers) ? 'Part-number research' : 'Material catalog research',
      'Material identification',
    ],
    keywords: source => [
      has(source, stockNumbers) && (has(source, /national stock|\bnsns?\b/i) ? 'national stock number research' : 'stock-number research'),
      has(source, partNumbers) && 'part-number research',
      'material identification', has(source, /catalog research/i) && 'catalog research',
    ],
  },
  {
    id: 'requisitions', heading: 'Orders and material requests',
    matches: source => has(source, ACTION) && has(source, material) && (has(source, ordering) || has(source, backordering) || has(source, /processed? (?:customer )?requests?/i)),
    bullet: source => has(source, ordering) && has(source, backordering)
      ? 'Supported material ordering and backorder processing.'
      : has(source, backordering) ? 'Supported material backorder processing.'
        : has(source, /requisitions?/i) ? 'Supported material requisition processing.'
          : has(source, ordering) ? 'Supported material order processing.' : 'Processed customer material requests.',
    skills: source => [
      has(source, /requisitions?/i) && 'Requisition processing',
      has(source, ordering) && has(source, backordering) ? 'Order and backorder coordination'
        : has(source, backordering) ? 'Backorder processing' : has(source, ordering) ? 'Material order processing' : 'Customer request processing',
    ],
    keywords: source => [
      has(source, /requisitions?/i) && 'requisitions', has(source, backordering) && 'backorders',
      has(source, ordering) && 'order processing', has(source, /customer requests?/i) && 'customer requests',
      has(source, /fulfill/i) && 'fulfillment',
    ],
  },
  {
    id: 'deployment-equipment', heading: 'Deployment equipment',
    matches: source => has(source, deployment) && has(source, ACTION),
    bullet: source => has(source, /inventory|inventoried/i)
      ? `Maintained inventory of deployment ${has(source, /bags?/i) ? 'bags' : 'equipment'}.`
      : has(source, /accountab/i) ? 'Supported accountability for deployment equipment.'
        : 'Supported deployment equipment and readiness logistics.',
    skills: source => ['Deployment-equipment support', has(source, /readiness/i) && 'Readiness logistics'],
    keywords: source => [
      has(source, /readiness/i) && 'deployment readiness', 'deployment equipment',
      has(source, /bags?/i) && 'deployment bags', has(source, /readiness logistics/i) && 'readiness logistics',
    ],
  },
  {
    id: 'protective-equipment', heading: 'Protective equipment logistics',
    matches: source => has(source, protective) && has(source, ACTION) && has(source, /inventor|accountab|storage|inspection|\bissue[ds]?\b|logistics|manag|support/i),
    bullet: source => {
      const equipment = has(source, /jslist/i) ? 'JSLIST protective equipment' : 'protective equipment';
      return has(source, /inventor/i) ? `Maintained inventory of ${equipment}.`
        : has(source, /accountab/i) ? `Supported accountability for ${equipment}.`
          : `Supported logistics for ${equipment}.`;
    },
    skills: source => [has(source, /inventory|accountab/i) && 'Protective-equipment accountability', 'Protective-equipment logistics'],
    keywords: source => [has(source, /jslist/i) && 'JSLIST equipment logistics', 'protective equipment', has(source, /accountab/i) && 'equipment accountability'],
  },
  {
    id: 'prioritization', heading: 'Material priorities',
    matches: source => has(source, material) && has(source, /prioriti[sz](?:ed|ing|e) (?:material )?requirements?/i),
    bullet: () => 'Prioritized material requirements.',
    skills: () => ['Requirements prioritization'],
    keywords: () => ['requirements prioritization', 'material requirements'],
  },
  {
    id: 'discrepancies', heading: 'Inventory discrepancies',
    matches: source => has(source, /supply|inventory|stock|material/i) && has(source, /(?:resolv\w*|research\w*|identif\w*|reconcil\w*)[\s\S]{0,50}(?:supply|inventory)?\s*discrepanc|reconcil\w*[\s\S]{0,30}(?:inventory|stock|material)/i),
    bullet: source => has(source, /resolv/i) ? 'Resolved supply discrepancies.'
      : has(source, /reconcil/i) ? 'Reconciled inventory records.'
        : has(source, /research/i) ? 'Researched inventory discrepancies.' : 'Identified inventory discrepancies.',
    skills: source => [has(source, /resolv/i) && 'Supply discrepancy resolution', has(source, /reconcil/i) && 'Inventory reconciliation', !has(source, /resolv|reconcil/i) && 'Inventory discrepancy research'],
    keywords: source => ['supply discrepancy', has(source, /reconcil/i) && 'inventory reconciliation', has(source, /resolv/i) && 'issue resolution'],
  },
  {
    id: 'maintainer-support', heading: 'Maintenance team support',
    matches: source => has(source, /maintainers?|maintenance teams?/i) && has(source, material) && has(source, /help|support|coordinat|provid/i),
    bullet: () => 'Supported maintainers with material requirements.',
    skills: source => [has(source, /coordinat/i) && 'Cross-functional maintenance coordination', 'Internal customer support'],
    keywords: source => ['maintainer support', has(source, /coordinat/i) && 'cross-functional coordination', has(source, /parts?|material|supply/i) && 'maintenance supply'],
  },
  {
    id: 'asset-status', heading: 'Supply status updates',
    matches: source => has(source, /asset[- ]status|order status|(?:material|supply)[- ]status/i) && has(source, /provid|report|communicat|track|updat/i),
    bullet: source => has(source, /provid|report|communicat/i)
      ? (has(source, /asset[- ]status/i) ? 'Provided asset-status information.' : has(source, /order status/i) ? 'Communicated material order status.' : 'Provided material-status information.')
      : has(source, /order status/i) ? 'Tracked material order status.' : 'Tracked asset-status information.',
    skills: source => [
      has(source, /asset[- ]status/i) && (has(source, /provid|report|communicat/i) ? 'Asset-status reporting' : 'Asset-status tracking'),
      has(source, /provid|report|communicat/i) ? 'Supply status communication' : 'Supply status tracking',
    ],
    keywords: source => [has(source, /asset[- ]status/i) && 'asset status', has(source, /order status/i) && 'order status', has(source, /provid|report|communicat/i) ? 'status reporting' : 'status tracking', has(source, /customer/i) && has(source, /provid|report|communicat/i) && 'customer communication'],
  },
];

export function buildTranslationDraft({ bio = '', occupation = null } = {}) {
  const narrative = typeof bio === 'string' ? bio.trim() : '';
  if (!narrative) return [];
  const sources = sourceUnits(narrative);
  const candidates = [];
  const seen = new Set();
  const add = (type, text, source, theme, heading, baseline = false) => {
    if (!text || !source) return;
    const key = `${type}:${text.trim().toLocaleLowerCase('en-US')}`;
    if (seen.has(key)) return;
    seen.add(key);
    candidates.push({
      id: stableId(type, theme, text), type, text, source, theme, heading,
      sourceLabel: type === 'titles' ? 'Career idea based on your details — not a role you held'
        : baseline ? 'From your specialty — confirm this fits' : 'From your service story',
    });
  };

  themeAdapters.forEach(adapter => {
    const rule = rulesById[adapter.id];
    sources.filter(adapter.matches).forEach(source => {
      add('bullets', adapter.bullet(source), source, adapter.id, adapter.heading);
      adapter.skills(source).forEach(text => add('skills', text, source, adapter.id, adapter.heading));
      // Titles are exploration options, never claims of employment or seniority.
      rule.titles.forEach(text => add('titles', text, source, adapter.id, adapter.heading));
      adapter.keywords(source).forEach(text => add('keywords', text, source, adapter.id, adapter.heading));
    });
  });

  const specialty = [occupation?.code, occupation?.title].filter(Boolean).join(' — ');
  const baseline = buildNarrativeSuggestions({ bio: narrative, occupation });
  if (specialty && baseline.skills.includes('Supply support')) {
    add('skills', 'Supply support', specialty, 'specialty', 'Your service specialty', true);
    add('titles', 'Supply Chain Specialist', specialty, 'specialty', 'Your service specialty', true);
    add('keywords', 'materiel management', specialty, 'specialty', 'Your service specialty', true);
    add('keywords', 'supply support', specialty, 'specialty', 'Your service specialty', true);
  }

  const order = { bullets: 0, skills: 1, titles: 2, keywords: 3 };
  return candidates.sort((a, b) => order[a.type] - order[b.type]);
}
